import { documentApi } from './api';
import { tokenStorage } from '../utils/tokenStorage';
import type { InstructionLetterData } from '../types';

// Processing Service HTTP client (goes through API Gateway like other services)
class ProcessingHttpClient {
  private baseURL: string;

  constructor() {
    // Processing Service should go through API Gateway, not direct to port 8084
    this.baseURL = '';
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = tokenStorage.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      
      // Add specific handling for HTTP 409 (session limit)
      if (response.status === 409) {
        throw new Error(`HTTP 409: ${errorMessage}`);
      }
      
      throw new Error(errorMessage);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // Handle text responses (like from the /process endpoint)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/plain')) {
      const text = await response.text();
      return text as T;
    }

    const jsonData = await response.json();
    
    // Unwrap ApiResponse wrapper if present (backend returns {success, data, message, error})
    // This handles the standardized API response format from the backend
    if (jsonData && typeof jsonData === 'object' && 'data' in jsonData && 'success' in jsonData) {
      return jsonData.data as T;
    }
    
    return jsonData;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async postBinary<T>(endpoint: string, data: ArrayBuffer, headers?: Record<string, string>): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = tokenStorage.getToken();

    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
      ...headers,
    };

    if (token) {
      reqHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: reqHeaders,
      body: data,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

const processingHttpClient = new ProcessingHttpClient();

// Types for processing API - aligned with backend DTOs
export interface ProcessingSession {
  id: string;
  caseId: string;
  userId: string;
  status: 'INITIALIZING' | 'EXTRACTING_TEXT' | 'IDENTIFYING_EVENTS' | 'RESOLVING_DATES' | 'BUILDING_TIMELINE' | 'VALIDATING_CITATIONS' | 'AWAITING_USER_INPUT' | 'COMPLETED' | 'FAILED';
  progress: number;
  currentStage: string;
  estimatedCompletion?: string;
  extractedEvents: number;
  pendingAmbiguities: number;
  confidenceDistribution: {
    high: number;
    medium: number;
    low: number;
    average: number;
  };
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface CreateSessionRequest {
  caseId: string;
  processingHints?: string;
}

export interface UpdateSessionStatusRequest {
  status: ProcessingSession['status'];
  progress?: number;
  currentStage?: string;
  errorMessage?: string;
}

export interface SessionStats {
  active: number;
  completed: number;
  failed: number;
  total: number;
}

export interface ProcessingHints {
  patientContext?: string;
  dateRange?: { start: string; end: string };
  medicalConditions?: string[];
  priorityEventTypes?: string[];
  processingInstructions?: string;
}

export interface ProcessingStage {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  estimatedTime?: string;
  details?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface DocumentProcessingStatus {
  documentId: string;
  filename: string;
  status: string;
  progress: number;
  stages: ProcessingStage[];
  textAnalysis?: {
    extractedText: string;
    totalCharacters: number;
    totalWords: number;
    totalLines: number;
    ocrConfidenceScore: number;
    characterPositions: any[];
  };
  integrity?: {
    isValid: boolean;
    contentHash: string;
    lastChecked: string;
  };
}

export interface Ambiguity {
  id: string;
  type: 'date' | 'event' | 'context';
  description: string;
  options: string[];
  context: string;
  documentId?: string;
  position?: { start: number; end: number };
}

export interface LocateTextResult {
  found: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Processing API service
export const processingApi = {
  // Create a new processing session
  createProcessingSession: async (caseId: string, hints?: ProcessingHints): Promise<ProcessingSession> => {
    
    try {
      const request: CreateSessionRequest = {
        caseId,
        processingHints: hints ? JSON.stringify(hints) : undefined
      };
      
      const response = await processingHttpClient.post<ProcessingSession>('/api/processing/sessions', request);
      return response;
    } catch (error) {
      console.error('❌ Failed to create processing session:', error);
      throw error;
    }
  },

  // Get processing session by ID
  getProcessingSession: async (sessionId: string): Promise<ProcessingSession> => {
    
    try {
      const response = await processingHttpClient.get<ProcessingSession>(`/api/processing/sessions/${sessionId}`);
      return response;
    } catch (error) {
      console.error('❌ Failed to get processing session:', error);
      throw error;
    }
  },

  // Get all processing sessions for current user
  getUserProcessingSessions: async (page = 0, size = 20): Promise<{
    content: ProcessingSession[];
    pageable: any;
    totalElements: number;
    totalPages: number;
  }> => {
    
    try {
      const response = await processingHttpClient.get<{
        content: ProcessingSession[];
        pageable: any;
        totalElements: number;
        totalPages: number;
      }>(`/api/processing/sessions?page=${page}&size=${size}`);
      return response;
    } catch (error) {
      console.error('❌ Failed to get user processing sessions:', error);
      throw error;
    }
  },

  // Get processing sessions for a specific case
  getCaseProcessingSessions: async (caseId: string): Promise<ProcessingSession[]> => {
    
    try {
      const response = await processingHttpClient.get<ProcessingSession[]>(`/api/processing/cases/${caseId}/sessions`);
      return response;
    } catch (error) {
      console.error('❌ Failed to get case processing sessions:', error);
      throw error;
    }
  },

  // Update processing session status
  updateSessionStatus: async (sessionId: string, request: UpdateSessionStatusRequest): Promise<ProcessingSession> => {
    
    try {
      const response = await processingHttpClient.put<ProcessingSession>(`/api/processing/sessions/${sessionId}/status`, request);
      return response;
    } catch (error) {
      console.error('❌ Failed to update session status:', error);
      throw error;
    }
  },

  // Delete processing session
  deleteProcessingSession: async (sessionId: string): Promise<void> => {
    
    try {
      await processingHttpClient.delete(`/api/processing/sessions/${sessionId}`);
    } catch (error) {
      console.error('❌ Failed to delete processing session:', error);
      throw error;
    }
  },

  // Get processing session statistics
  getSessionStats: async (): Promise<SessionStats> => {
    
    try {
      const response = await processingHttpClient.get<SessionStats>('/api/processing/sessions/stats');
      return response;
    } catch (error) {
      console.error('❌ Failed to get session statistics:', error);
      throw error;
    }
  },

  // Get case documents with processing status (enhanced with session data)
  getCaseDocumentsWithStatus: async (caseId: string): Promise<DocumentProcessingStatus[]> => {
    
    try {
      // Get documents from document service
      const documentsResponse = await documentApi.getCaseDocuments(caseId) as any;
      const documents = Array.isArray(documentsResponse) ? documentsResponse : documentsResponse?.documents || [];
      
      
      // If no documents, return empty array
      if (documents.length === 0) {
        return [];
      }
      
      // Get processing sessions for this case (but don't use the variable)
      try {
        await processingApi.getCaseProcessingSessions(caseId);
      } catch (error) {
      }
      
      // Get detailed processing status for each document
      const documentsWithStatus = await Promise.all(
        documents.map(async (doc: any) => {
          try {
            // Get detailed processing status
            const statusResponse = await documentApi.getDetailedProcessingStatus(doc.documentId) as any;
            
            // Get text analysis if available
            let textAnalysis: any;
            try {
              textAnalysis = await documentApi.getDocumentTextAnalysis(doc.documentId);
            } catch (error) {
            }
            
            // Skip integrity validation - documents are trusted by default
            
            // Map backend status to frontend format
            const mapStatus = (backendStatus: string) => {
              const statusMap: { [key: string]: string } = {
                'UPLOADED': 'uploaded',
                'READY_FOR_PROCESSING': 'ready',
                'OCR_PROCESSING': 'processing',
                'TEXT_EXTRACTED': 'processed',
                'COMPLETE': 'complete',
                'ERROR': 'error'
              };
              return statusMap[backendStatus] || backendStatus.toLowerCase();
            };
            
            return {
              documentId: doc.documentId,
              filename: doc.filename,
              status: mapStatus(statusResponse?.currentStatus || 'uploaded'),
              progress: statusResponse?.overallProgress || 0,
              stages: (statusResponse?.stages || []).map((stage: any) => ({
                id: (stage.name || 'unknown').toLowerCase().replace(/\s+/g, '-'),
                name: stage.name || 'Unknown Stage',
                description: stage.name || 'Unknown Stage',
                status: stage.status === 'COMPLETED' ? 'completed' : 
                       stage.status === 'IN_PROGRESS' ? 'running' : 
                       stage.status === 'ERROR' ? 'failed' : 'pending',
                progress: stage.progress || 0,
                estimatedTime: statusResponse?.estimatedCompletion ? 
                  new Date(statusResponse.estimatedCompletion).toLocaleTimeString() : undefined,
                details: stage.errorMessage || undefined,
                startedAt: stage.startedAt,
                completedAt: stage.completedAt
              })),
              textAnalysis
            };
          } catch (error) {
            console.error('Failed to get processing status for document:', doc.documentId, error);
            return {
              documentId: doc.documentId,
              filename: doc.filename,
              status: doc.processingStatus?.toLowerCase() || 'uploaded',
              progress: 0,
              stages: []
            };
          }
        })
      );
      
      return documentsWithStatus;
    } catch (error) {
      console.error('❌ Failed to get case documents with status:', error);
      // Return empty array instead of throwing error
      return [];
    }
  },

  // Start AI processing for a session with multimodal support
  startAIProcessingMultimodal: async (sessionId: string, documentFiles?: Array<{filename: string, contentType: string, content: string, documentId?: string}>): Promise<string> => {
    
    try {
      const requestData = {
        documentFiles: documentFiles || []
      };
      
      const response = await processingHttpClient.post<string>(`/api/processing/sessions/${sessionId}/process`, requestData);
      return response;
    } catch (error) {
      console.error('❌ Failed to start multimodal AI processing:', error);
      throw error;
    }
  },

  // Start AI processing for a session (legacy text-based)
  startAIProcessing: async (sessionId: string, documentTexts?: string[]): Promise<string> => {
    
    try {
      // Send as direct array for backward compatibility
      const response = await processingHttpClient.post<string>(`/api/processing/sessions/${sessionId}/process`, documentTexts || []);
      return response;
    } catch (error) {
      console.error('❌ Failed to start AI processing:', error);
      throw error;
    }
  },

  // Start comprehensive processing for a case
  startProcessing: async (caseId: string, hints?: ProcessingHints): Promise<ProcessingSession> => {
    
    try {
      // Create a processing session - the Processing Service will handle the workflow
      const session = await processingApi.createProcessingSession(caseId, hints);
      
      return session;
    } catch (error) {
      console.error('❌ Failed to start processing:', error);
      throw error;
    }
  },

  // Get processing progress for a session (enhanced with real data)
  getProcessingProgress: async (sessionId: string): Promise<{
    session: ProcessingSession;
    stages: ProcessingStage[];
    ambiguities: Ambiguity[];
  }> => {
    
    try {
      const session = await processingApi.getProcessingSession(sessionId);
      
      // Map session status to processing stages
      const stages: ProcessingStage[] = [
        {
          id: 'initializing',
          name: 'Initialising',
          description: 'Setting up processing session',
          status: session.status === 'INITIALIZING' ? 'running' : 
                 ['EXTRACTING_TEXT', 'IDENTIFYING_EVENTS', 'RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 'completed' : 'pending',
          progress: session.status === 'INITIALIZING' ? session.progress : 
                   ['EXTRACTING_TEXT', 'IDENTIFYING_EVENTS', 'RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 100 : 0
        },
        {
          id: 'text-extraction',
          name: 'Document Analysis',
          description: 'Reading and analysing document content',
          status: session.status === 'EXTRACTING_TEXT' ?'running' : 
                 ['IDENTIFYING_EVENTS', 'RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 'completed' : 'pending',
          progress: session.status === 'EXTRACTING_TEXT' ? session.progress : 
                   ['IDENTIFYING_EVENTS', 'RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 100 : 0
        },
        {
          id: 'event-identification',
          name: 'Event Identification',
          description: 'AI identifying medical events and entities',
          status: session.status === 'IDENTIFYING_EVENTS' ? 'running' : 
                 ['RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 'completed' : 'pending',
          progress: session.status === 'IDENTIFYING_EVENTS' ? session.progress : 
                   ['RESOLVING_DATES', 'BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 100 : 0
        },
        {
          id: 'date-resolution',
          name: 'Date Resolution',
          description: 'Resolving temporal references and dates',
          status: session.status === 'RESOLVING_DATES' ? 'running' : 
                 ['BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 'completed' : 'pending',
          progress: session.status === 'RESOLVING_DATES' ? session.progress : 
                   ['BUILDING_TIMELINE', 'VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 100 : 0
        },
        {
          id: 'timeline-building',
          name: 'Timeline Building',
          description: 'Constructing chronological timeline',
          status: session.status === 'BUILDING_TIMELINE' ? 'running' : 
                 ['VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 'completed' : 'pending',
          progress: session.status === 'BUILDING_TIMELINE' ? session.progress : 
                   ['VALIDATING_CITATIONS', 'COMPLETED'].includes(session.status) ? 100 : 0
        },
        {
          id: 'citation-validation',
          name: 'Citation Validation',
          description: 'Validating citations and references',
          status: session.status === 'VALIDATING_CITATIONS' ? 'running' : 
                 session.status === 'COMPLETED' ? 'completed' : 'pending',
          progress: session.status === 'VALIDATING_CITATIONS' ? session.progress : 
                   session.status === 'COMPLETED' ? 100 : 0
        }
      ];
      
      // Get real ambiguities from session data
      const ambiguities: Ambiguity[] = [];
      
      return {
        session,
        stages,
        ambiguities
      };
    } catch (error) {
      console.error('❌ Failed to get processing progress:', error);
      throw error;
    }
  },

  // Subscribe to real-time processing updates (placeholder for WebSocket)
  subscribeToProcessingUpdates: (sessionId: string, onUpdate: (session: ProcessingSession) => void) => {
    
    // For now, poll the session status
    // This will be replaced with WebSocket connection in future iterations
    const interval = setInterval(async () => {
      try {
        const session = await processingApi.getProcessingSession(sessionId);
        onUpdate(session);
        
        // Stop polling if session is complete or failed
        if (session.status === 'COMPLETED' || session.status === 'FAILED') {
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Failed to poll session status:', error);
      }
    }, 3000);
    
    return () => {
      clearInterval(interval);
    };
  },

  // Simplified case validation - documents are trusted by default
  validateCaseIntegrity: async (caseId: string, _retryCount = 0): Promise<{
    caseId: string;
    overallValid: boolean;
    documents: Array<{
      documentId: string;
      filename: string;
      isValid: boolean;
      lastChecked: string;
      status: 'valid' | 'invalid' | 'pending' | 'error' | 'not_found';
      issues: Array<{ type: string; message: string }>;
    }>;
  }> => {
    
    try {
      const documentsResponse = await documentApi.getCaseDocuments(caseId) as any;
      const documents = Array.isArray(documentsResponse) ? documentsResponse : documentsResponse?.documents || [];
      
      if (documents.length === 0) {
        return {
          caseId,
          overallValid: true,
          documents: []
        };
      }
      
      // All documents are considered valid by default
      const integrityResults = documents.map((doc: any) => ({
        documentId: doc.documentId,
        filename: doc.filename,
        isValid: true,
        lastChecked: new Date().toISOString(),
        status: 'valid' as const,
        issues: []
      }));
      
      
      return {
        caseId,
        overallValid: true,
        documents: integrityResults
      };
    } catch (error) {
      console.error('❌ Failed to get documents for case:', caseId, error);
      return {
        caseId,
        overallValid: true,
        documents: []
      };
    }
  },

  // Extract text from a file (PDF or other) via the processing service
  extractTextFromFile: async (file: File, skipBilling = false): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const headers: Record<string, string> = { 'X-Filename': file.name };
    if (skipBilling) headers['X-Skip-Billing'] = 'true';
    const result = await processingHttpClient.postBinary<{ fullMarkdown?: string }>(
      '/api/processing/extract-structured',
      buffer,
      headers
    );
    return result.fullMarkdown || '';
  },

  // Extract structured case context from a solicitor's instruction letter
  extractInstructionLetter: async (documentText: string, filename: string): Promise<InstructionLetterData> => {
    try {
      const response = await processingHttpClient.post<InstructionLetterData>(
        '/api/processing/extract-instruction-letter',
        { documentText, filename }
      );
      return response;
    } catch (error) {
      console.error('Failed to extract instruction letter:', error);
      throw error;
    }
  },

  // Get processing statistics for a case (enhanced with session data)
  getProcessingStats: async (caseId: string): Promise<{
    totalDocuments: number;
    processedDocuments: number;
    totalEvents: number;
    averageConfidence: number;
    flaggedItems: number;
    activeSessions: number;
    completedSessions: number;
  }> => {
    
    try {
      const [documentsWithStatus, processingSessions] = await Promise.all([
        processingApi.getCaseDocumentsWithStatus(caseId),
        processingApi.getCaseProcessingSessions(caseId).catch(() => [])
      ]);
      
      const totalDocuments = documentsWithStatus.length;
      const processedDocuments = documentsWithStatus.filter(doc => 
        doc.status === 'complete' || doc.status === 'processed'
      ).length;
      
      // Calculate average confidence from text analysis
      const confidenceScores = documentsWithStatus
        .filter(doc => doc.textAnalysis?.ocrConfidenceScore !== undefined)
        .map(doc => Number(doc.textAnalysis!.ocrConfidenceScore) || 0);
      
      const averageConfidence = confidenceScores.length > 0 
        ? confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length
        : 0;
      
      // Count documents with integrity issues
      const integrityIssues = documentsWithStatus.filter(doc => 
        doc.integrity && !doc.integrity.isValid
      ).length;
      
      // Calculate events from processing sessions
      const totalEvents = (processingSessions as ProcessingSession[]).reduce((sum: number, session: ProcessingSession) => sum + (Number(session.extractedEvents) || 0), 0);
      
      // Count active and completed sessions
      const activeSessions = processingSessions.filter(session => 
        !['COMPLETED', 'FAILED'].includes(session.status)
      ).length;
      
      const completedSessions = processingSessions.filter(session => 
        session.status === 'COMPLETED'
      ).length;
      
      const stats = {
        totalDocuments,
        processedDocuments,
        totalEvents,
        averageConfidence: Math.round(averageConfidence * 100),
        flaggedItems: integrityIssues,
        activeSessions,
        completedSessions
      };
      
      return stats;
    } catch (error) {
      console.error('❌ Failed to get processing stats:', error);
      throw error;
    }
  },

  // Locate text on a scanned PDF page (on-demand bounding box for citation highlighting)
  locateText: async (documentId: string, pageNumber: number, searchText: string): Promise<LocateTextResult> => {
    try {
      return await processingHttpClient.post<LocateTextResult>(
        '/api/processing/locate-text',
        { documentId, pageNumber, searchText }
      );
    } catch (error) {
      console.error('Failed to locate text:', error);
      return { found: false, x: 0, y: 0, width: 0, height: 0 };
    }
  },

  // Hybrid search: vector similarity + full-text keyword matching
  searchChunks: async (caseId: string, query: string, topK = 10): Promise<{ documentId: string; pageNumber: number | null; endPageNumber: number | null; content: string; chunkIndex: number; score: number; hasKeywordMatch: boolean }[]> => {
    try {
      const response = await processingHttpClient.post<{ chunks: { documentId: string; pageNumber: number | null; endPageNumber: number | null; content: string; chunkIndex: number; score: number; hasKeywordMatch: boolean }[] }>(
        `/api/processing/chunking/cases/${caseId}/search`,
        { query, topK }
      );
      return response.chunks;
    } catch (error) {
      console.error('Failed to search chunks:', error);
      return [];
    }
  },

  // Report a citation highlight failure to the backend for structured logging
  reportCitationFailure: async (params: {
    documentId: string;
    pageNumber: number;
    searchText: string;
    textLayerSearched: boolean;
    boundingBoxSearched: boolean;
    hasStoredGeometry: boolean;
  }): Promise<void> => {
    try {
      await processingHttpClient.post<void>(
        '/api/processing/citation-failure',
        params
      );
    } catch {
      // Best-effort logging — silently swallow errors
    }
  }
};